import {
  addPage,
  addPlacement,
  bumpRevision,
  changeLayer,
  createAlbum,
  createDefaultSettings,
  normalizeSettings,
  removePage,
  removePlacement,
  renameAlbum,
  reorderPage,
  setCover,
  updatePlacement,
} from "./lib/commands.ts";
import { createId, nowIso } from "./lib/ids.ts";
import type {
  AlbumDocument,
  AppSettings,
  FurnitureGeometry,
  Placement,
  RoomFurnitureLayout,
  RoomFurnitureSlot,
  RoomImageAssets,
  RoomImageSlot,
} from "./lib/types.ts";
import * as db from "./storage/indexeddb.ts";
import {
  discardImportedAsset,
  finalizeImportedAsset,
  garbageCollectAssets,
  reconcilePendingAssets,
} from "./storage/assets.ts";
import { seedLibraryIfNeeded, SEED_META_KEY } from "./storage/seed.ts";
import { DEFAULT_ROOM_FURNITURE_LAYOUT } from "./room/furniture.ts";

type Listener = () => void;

interface AlbumHistory {
  undo: AlbumDocument[];
  redo: AlbumDocument[];
}

type AppSettingsPatch = Omit<Partial<AppSettings>, "roomImages" | "roomFurnitureLayout"> & {
  roomImages?: Partial<RoomImageAssets>;
  roomFurnitureLayout?: Partial<RoomFurnitureLayout>;
};

class AppStore {
  private listeners = new Set<Listener>();
  private history = new Map<string, AlbumHistory>();
  private writeChain = Promise.resolve();
  settings: AppSettings | null = null;
  albums: AlbumDocument[] = [];
  loading = true;
  error: string | null = null;
  lastError: string | null = null;

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  private historyFor(albumId: string): AlbumHistory {
    let entry = this.history.get(albumId);
    if (!entry) {
      entry = { undo: [], redo: [] };
      this.history.set(albumId, entry);
    }
    return entry;
  }

  private pushUndo(album: AlbumDocument): void {
    const stack = this.historyFor(album.id);
    stack.undo.push(structuredClone(album));
    if (stack.undo.length > 50) stack.undo.shift();
    stack.redo = [];
  }

  private enqueueWrite<T>(albumId: string, task: () => Promise<T>): Promise<T> {
    const run = async () => db.withWriteLock(albumId, task);
    const next = this.writeChain.then(run, run);
    this.writeChain = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }

  async init(): Promise<void> {
    try {
      await reconcilePendingAssets();
      const settings = await db.withWriteLock("settings", async () => {
        const persisted = await db.getSettings();
        const normalized = normalizeSettings(
          persisted ?? createDefaultSettings(createId()),
        );
        await db.putSettings(normalized);
        return normalized;
      });
      this.settings = settings;
      if (sessionStorage.getItem("skip-seed") === "1") {
        sessionStorage.removeItem("skip-seed");
        if (!(await db.getMeta(SEED_META_KEY))) {
          await db.putMeta(SEED_META_KEY, { skipped: true });
        }
      } else {
        await seedLibraryIfNeeded();
      }
      this.albums = await db.listAlbums();
      try {
        await garbageCollectAssets();
      } catch (error) {
        this.lastError =
          error instanceof Error ? error.message : "Failed to clean up unused images";
      }
      await db.requestPersistentStorage();
    } catch (error) {
      this.error = error instanceof Error ? error.message : "Failed to initialize storage";
    } finally {
      this.loading = false;
      this.notify();
    }
  }

  async refreshAlbums(): Promise<void> {
    this.albums = await db.listAlbums();
    this.notify();
  }

  async createAlbum(title?: string): Promise<AlbumDocument> {
    const album = createAlbum(title);
    await this.enqueueWrite(album.id, async () => {
      await db.putAlbum(album);
    });
    await this.refreshAlbums();
    return album;
  }

  private async persistAlbum(
    album: AlbumDocument,
    options: { recordUndo?: boolean; previous?: AlbumDocument } = {},
  ): Promise<AlbumDocument> {
    const { recordUndo = true, previous } = options;
    return this.enqueueWrite(album.id, async () => {
      const current = previous ?? (await db.getAlbum(album.id));
      if (current && recordUndo) this.pushUndo(current);
      const next = bumpRevision(album);
      await db.putAlbum(next);
      await garbageCollectAssets();
      await this.refreshAlbums();
      return next;
    });
  }

  async updateAlbum(
    albumId: string,
    mutator: (album: AlbumDocument) => AlbumDocument,
    recordUndo = true,
  ): Promise<AlbumDocument | null> {
    try {
      const current = await db.getAlbum(albumId);
      if (!current) return null;
      const mutated = mutator(current);
      return await this.persistAlbum(mutated, { recordUndo, previous: current });
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : "Failed to save album";
      this.notify();
      throw error;
    }
  }

  async commitPlacementPreview(
    albumId: string,
    pageId: string,
    placementId: string,
    patch: Partial<Placement>,
    snapshotBefore: AlbumDocument,
  ): Promise<AlbumDocument | null> {
    try {
      return await this.enqueueWrite(albumId, async () => {
        const current = await db.getAlbum(albumId);
        if (!current) return null;
        this.pushUndo(snapshotBefore);
        const next = updatePlacement(current, pageId, placementId, patch);
        await db.putAlbum(next);
        await this.refreshAlbums();
        return next;
      });
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : "Failed to save placement";
      this.notify();
      throw error;
    }
  }

  async undo(albumId: string): Promise<void> {
    const stack = this.historyFor(albumId);
    const snapshot = stack.undo.pop();
    if (!snapshot) return;
    await this.enqueueWrite(albumId, async () => {
      const current = await db.getAlbum(albumId);
      if (current) stack.redo.push(current);
      await db.putAlbum(snapshot);
      await this.refreshAlbums();
    });
  }

  async redo(albumId: string): Promise<void> {
    const stack = this.historyFor(albumId);
    const snapshot = stack.redo.pop();
    if (!snapshot) return;
    await this.enqueueWrite(albumId, async () => {
      const current = await db.getAlbum(albumId);
      if (current) stack.undo.push(current);
      await db.putAlbum(snapshot);
      await this.refreshAlbums();
    });
  }

  async deleteAlbumById(albumId: string): Promise<void> {
    await this.enqueueWrite(albumId, async () => {
      await db.deleteAlbum(albumId);
      this.history.delete(albumId);
      await garbageCollectAssets();
      await this.refreshAlbums();
    });
  }

  async updateSettings(patch: AppSettingsPatch): Promise<void> {
    if (!this.settings) return;
    try {
      await this.enqueueWrite("settings", async () => {
        const inMemory = this.settings;
        if (!inMemory) return;
        const persisted = await db.getSettings();
        const current = normalizeSettings(persisted ?? inMemory);
        const next = normalizeSettings({
          ...current,
          ...patch,
          roomImages: patch.roomImages
            ? { ...current.roomImages, ...patch.roomImages }
            : current.roomImages,
          roomFurnitureLayout: patch.roomFurnitureLayout
            ? { ...current.roomFurnitureLayout, ...patch.roomFurnitureLayout }
            : current.roomFurnitureLayout,
        });
        await db.putSettings(next);
        this.settings = next;
        this.notify();
        if (patch.roomImages) {
          try {
            await garbageCollectAssets();
          } catch (error) {
            this.lastError =
              error instanceof Error
                ? error.message
                : "Failed to clean up unused room images";
            this.notify();
          }
        }
      });
    } catch (error) {
      this.lastError =
        error instanceof Error ? error.message : "Failed to save settings";
      this.notify();
      throw error;
    }
  }

  private async finalizeImport(assetId: string): Promise<void> {
    try {
      await finalizeImportedAsset(assetId);
    } catch (error) {
      this.lastError =
        error instanceof Error ? error.message : "Failed to finalize imported image";
      this.notify();
    }
  }

  private async discardImport(assetId: string): Promise<void> {
    try {
      await discardImportedAsset(assetId);
    } catch {
      // Keep the primary save error; init recovery will retry orphan cleanup.
    }
  }

  async setRoomImage(slot: RoomImageSlot, assetId: string | null): Promise<void> {
    if (!this.settings) {
      if (assetId) await this.discardImport(assetId);
      throw new Error("Settings are not initialized");
    }
    if (!assetId) {
      await this.updateSettings({ roomImages: { [slot]: null } });
      return;
    }
    try {
      await this.updateSettings({ roomImages: { [slot]: assetId } });
    } catch (error) {
      await this.discardImport(assetId);
      throw error;
    }
    await this.finalizeImport(assetId);
  }

  restoreRoomImage(slot: RoomImageSlot): Promise<void> {
    return this.setRoomImage(slot, null);
  }

  restoreAllRoomImages(): Promise<void> {
    return this.updateSettings({
      roomImages: {
        room: null,
        shelf: null,
        fireplace: null,
        table: null,
      },
    });
  }

  setRoomFurnitureGeometry(
    slot: RoomFurnitureSlot,
    geometry: FurnitureGeometry,
  ): Promise<void> {
    return this.updateSettings({ roomFurnitureLayout: { [slot]: geometry } });
  }

  restoreRoomFurnitureLayout(): Promise<void> {
    return this.updateSettings({
      roomFurnitureLayout: structuredClone(DEFAULT_ROOM_FURNITURE_LAYOUT),
    });
  }

  async addPlacement(
    albumId: string,
    pageId: string,
    assetId: string,
  ): Promise<AlbumDocument | null> {
    try {
      const album = await this.updateAlbum(albumId, (current) =>
        addPlacement(current, pageId, assetId),
      );
      if (!album) {
        await this.discardImport(assetId);
        return null;
      }
      await this.finalizeImport(assetId);
      return album;
    } catch (error) {
      await this.discardImport(assetId);
      throw error;
    }
  }

  rename = (albumId: string, title: string) => this.updateAlbum(albumId, (a) => renameAlbum(a, title));

  addPage = (albumId: string) => this.updateAlbum(albumId, (a) => addPage(a));

  removePage = (albumId: string, pageId: string) =>
    this.updateAlbum(albumId, (a) => removePage(a, pageId));

  reorderPage = (albumId: string, pageId: string, toIndex: number) =>
    this.updateAlbum(albumId, (a) => reorderPage(a, pageId, toIndex));

  removePlacement = (albumId: string, pageId: string, placementId: string) =>
    this.updateAlbum(albumId, (a) => removePlacement(a, pageId, placementId));

  changeLayer = (albumId: string, pageId: string, placementId: string, delta: number) =>
    this.updateAlbum(albumId, (a) => changeLayer(a, pageId, placementId, delta));

  setCover = (albumId: string, assetId: string | null) =>
    this.updateAlbum(albumId, (a) => setCover(a, assetId));
}

export const store = new AppStore();
